"use client"

import { useState, useEffect, useCallback } from "react"
import type { DailyEntry } from "@/lib/db/schema"
import { getZoneById } from "@/lib/zones"
import { TrendingUp, TrendingDown, Minus, Sparkles, Loader2, AlertCircle, RefreshCw } from "lucide-react"
import { motion } from "framer-motion"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ProgressIndicator } from "@/components/progress-indicator"
import { TldrSummary } from "@/components/tldr-summary"
import { parsePatternResponse } from "@/lib/ai/utils"

// Fun error messages that rotate randomly
const ERROR_MESSAGES = [
  "🤖 AI právě odpočívá. Zkus to prosím znovu!",
  "🌙 AI si dává pauzu. Klikni na tlačítko a zkusíme to ještě jednou.",
  "☕ AI si dává kafíčko. Chceš to zkusit znovu?",
  "🎯 Asi jsme to trochu přehnali s energií. Zkus to znovu?",
  "🔄 Něco se nepovedlo, ale nevadí! Zkusíme to znovu?",
  "🌟 Hvězdy se ještě nesešly správně. Zkusíme to znovu?",
  "🔮 Krystalová koule se zamlžila. Zkus to prosím ještě jednou!",
]

function getRandomErrorMessage(): string {
  return ERROR_MESSAGES[Math.floor(Math.random() * ERROR_MESSAGES.length)]
}

interface PatternInsightsProps {
  entries: DailyEntry[]
}

interface ParsedInsights {
  metrics: import("@/lib/ai/utils").MetricInsight[]
  fullAnalysis: string
  hasTldr: boolean
}

export function PatternInsights({ entries }: PatternInsightsProps) {
  const [aiInsights, setAiInsights] = useState<string>("")
  const [parsedInsights, setParsedInsights] = useState<ParsedInsights | null>(null)
  const [loadingAI, setLoadingAI] = useState(true)
  const [errorAI, setErrorAI] = useState(false)
  const [errorId, setErrorId] = useState<string | null>(null)
  const [retryTrigger, setRetryTrigger] = useState(0)

  // Retry handler - clears cache and triggers re-fetch
  const handleRetry = useCallback(() => {
    // Reset error state
    setErrorAI(false)
    setLoadingAI(true)
    setAiInsights("")
    setParsedInsights(null)
    setErrorId(null)

    // Clear cache to force fresh fetch
    const userId = entries[0]?.userId || 'anonymous'
    const cacheKey = `pattern-cache-v1-${userId}-${entries.length}`
    try {
      localStorage.removeItem(cacheKey)
    } catch (e) {
      // Ignore localStorage errors
    }

    // Trigger re-fetch by updating state
    setRetryTrigger((prev) => prev + 1)
  }, [entries])

  if (entries.length === 0) return null

  // Calculate statistical insights (keep existing logic)
  const zoneFrequency = entries.reduce(
    (acc, entry) => {
      acc[entry.zoneId] = (acc[entry.zoneId] || 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )

  const mostFrequent = Object.entries(zoneFrequency).sort(([, a], [, b]) => b - a)[0]
  const mostFrequentZone = mostFrequent ? getZoneById(mostFrequent[0]) : null

  // Last 7 days trend
  const last7Days = entries.slice(0, 7)
  const previous7Days = entries.slice(7, 14)

  const getAverageNumber = (entries: DailyEntry[]) => {
    const numbers = entries
      .map((e) => {
        const zone = getZoneById(e.zoneId)
        return zone && typeof zone.number === "number" ? zone.number : null
      })
      .filter((n): n is number => n !== null)

    return numbers.length > 0 ? numbers.reduce((a, b) => a + b, 0) / numbers.length : null
  }

  const recentAvg = getAverageNumber(last7Days)
  const previousAvg = getAverageNumber(previous7Days)

  let trend: "up" | "down" | "stable" = "stable"
  if (recentAvg !== null && previousAvg !== null) {
    if (recentAvg > previousAvg + 0.5) trend = "up"
    else if (recentAvg < previousAvg - 0.5) trend = "down"
  }

  const statisticalInsights = []

  if (mostFrequentZone) {
    statisticalInsights.push({
      icon: mostFrequentZone.emoji,
      text: `Nejčastěji se nacházíš v zóně ${mostFrequentZone.name.split("/")[0].trim()}`,
      count: `${mostFrequent[1]}× za posledních ${entries.length} dní`,
    })
  }

  if (trend !== "stable" && recentAvg !== null) {
    statisticalInsights.push({
      icon:
        trend === "up" ? (
          <TrendingUp className="h-4 w-4 text-orange-600" />
        ) : (
          <TrendingDown className="h-4 w-4 text-blue-600" />
        ),
      text:
        trend === "up"
          ? "Energie roste – sleduj hranice, nezapomeň na regeneraci"
          : "Energie klesá – možná je čas zpomalit a doplnit síly",
      count: `Průměr: ${recentAvg.toFixed(1)}`,
    })
  } else if (recentAvg !== null) {
    statisticalInsights.push({
      icon: <Minus className="h-4 w-4 text-muted-foreground" />,
      text: "Stav zůstává stabilní – věnuj pozornost udržení rytmu",
      count: `Průměr: ${recentAvg.toFixed(1)}`,
    })
  }

  // Fetch AI-powered pattern analysis
  useEffect(() => {
    let aborted = false

    const fetchAIPatterns = async () => {
      try {
        setLoadingAI(true)
        setErrorAI(false)
        setAiInsights("")

        // Check localStorage cache first (include user data in key for better isolation)
        const userId = entries[0]?.userId || 'anonymous'
        const cacheKey = `pattern-cache-v1-${userId}-${entries.length}`
        const cached = localStorage.getItem(cacheKey)

        if (cached) {
          try {
            const cacheData = JSON.parse(cached)
            const isExpired = Date.now() - cacheData.timestamp > 60 * 60 * 1000 // 1 hour TTL

            if (!isExpired && cacheData.entryCount === entries.length) {
              // Use cached response
              setAiInsights(cacheData.message)

              // Parse the cached message
              const parsed = parsePatternResponse(cacheData.message)
              setParsedInsights(parsed)

              // Debug logging
              if (process.env.NODE_ENV === 'development') {
                console.log('Using cached AI response:', {
                  hasTldr: parsed.hasTldr,
                  metricsCount: parsed.metrics.length,
                  metrics: parsed.metrics,
                  rawMessage: cacheData.message.substring(0, 200) + '...',
                })
              }

              setLoadingAI(false)
              return
            }
          } catch (parseError) {
            // Invalid cache, continue to fetch
            console.warn("Invalid pattern cache, fetching fresh data")
          }
        }

        const response = await fetch("/api/ai/patterns", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            days: Math.max(7, Math.min(entries.length, 30)),
          }),
        })

        if (!response.ok) {
          // Extract error ID from response headers
          const backendErrorId = response.headers.get("X-Error-Id")
          if (backendErrorId) {
            setErrorId(backendErrorId)
          }

          // Try to parse error response
          let errorMessage = "AI patterns analysis failed"
          let validationDetails = null

          try {
            const contentType = response.headers.get("content-type")

            if (contentType?.includes("application/json")) {
              const errorData = await response.json()
              errorMessage = errorData.error || errorMessage
              validationDetails = errorData.details

              // Also check for errorId in response body
              if (errorData.errorId && !backendErrorId) {
                setErrorId(errorData.errorId)
              }
            } else {
              const textError = await response.text()
              if (textError) errorMessage = textError
            }
          } catch (parseError) {
            // If parsing fails, use default message
            console.error("Failed to parse error response:", parseError)
          }

          // Log specific error details for debugging (including correlation ID)
          console.error("AI patterns API error:", {
            status: response.status,
            statusText: response.statusText,
            message: errorMessage,
            errorId: backendErrorId || errorId,
            validationDetails,
          })

          // Handle specific error cases
          if (response.status === 401) {
            console.error("  Issue: Authentication required - user not signed in")
          } else if (response.status === 429) {
            console.error("  Issue: Rate limit exceeded")
          }

          throw new Error(errorMessage)
        }

        // Handle streaming response
        const reader = response.body?.getReader()
        const decoder = new TextDecoder()

        if (!reader) {
          throw new Error("No response body")
        }

        let accumulated = ""

        while (true) {
          const { done, value } = await reader.read()

          if (done || aborted) {
            break
          }

          const chunk = decoder.decode(value, { stream: true })
          accumulated += chunk

          if (!aborted) {
            setAiInsights(accumulated)
            // Parse on each chunk to update UI progressively
            const parsed = parsePatternResponse(accumulated)
            setParsedInsights(parsed)

            // Debug logging
            if (process.env.NODE_ENV === 'development') {
              console.log('Parsed AI response:', {
                hasTldr: parsed.hasTldr,
                metricsCount: parsed.metrics.length,
                metrics: parsed.metrics,
              })
            }
          }
        }

        // Save to localStorage cache
        if (!aborted && accumulated) {
          const userId = entries[0]?.userId || 'anonymous'
          const cacheKey = `pattern-cache-v1-${userId}-${entries.length}`
          try {
            localStorage.setItem(
              cacheKey,
              JSON.stringify({
                userId,
                entryCount: entries.length,
                message: accumulated,
                timestamp: Date.now(),
                version: 1,
              }),
            )
          } catch (storageError) {
            console.warn("Failed to cache pattern insights:", storageError)
          }
        }

        setLoadingAI(false)
      } catch (err) {
        console.error("Error fetching AI pattern analysis:", err)

        if (!aborted) {
          setErrorAI(true)
          setLoadingAI(false)
        }
      }
    }

    // Only fetch if we have enough entries (minimum 3 for pattern detection)
    if (entries.length >= 3) {
      fetchAIPatterns()
    } else {
      setLoadingAI(false)
    }

    return () => {
      aborted = true
    }
  }, [entries.length, retryTrigger])

  return (
    <div className="space-y-4">
      {/* Statistical Insights */}
      <div className="space-y-2 sm:space-y-3">
        {statisticalInsights.map((insight, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex items-start gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-lg bg-muted/50"
          >
            <div className="text-lg sm:text-xl shrink-0 mt-0.5">
              {typeof insight.icon === "string" ? insight.icon : insight.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-pretty leading-relaxed">{insight.text}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">{insight.count}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Progress Indicator - Show when user has 1-2 entries */}
      {entries.length > 0 && entries.length < 3 && <ProgressIndicator currentCount={entries.length} targetCount={3} />}

      {/* AI-Powered Insights */}
      {entries.length >= 3 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                {loadingAI && !aiInsights ? (
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                ) : errorAI ? (
                  <AlertCircle className="h-4 w-4 text-primary" />
                ) : (
                  <Sparkles className="h-4 w-4 text-primary" />
                )}
                <span>Hlubší analýza vzorců</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {loadingAI && !aiInsights ? (
                <div className="space-y-2">
                  <div className="h-3 bg-primary/10 rounded animate-pulse w-full" />
                  <div className="h-3 bg-primary/10 rounded animate-pulse w-5/6" />
                  <div className="h-3 bg-primary/10 rounded animate-pulse w-4/5" />
                </div>
              ) : errorAI ? (
                <div className="space-y-3">
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {getRandomErrorMessage()}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRetry}
                    disabled={loadingAI}
                    className="text-xs gap-2"
                  >
                    {loadingAI ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Načítám...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-3 w-3" />
                        Zkusit znovu
                      </>
                    )}
                  </Button>
                </div>
              ) : aiInsights ? (
                parsedInsights && parsedInsights.hasTldr ? (
                  <TldrSummary metrics={parsedInsights.metrics} fullAnalysis={parsedInsights.fullAnalysis} />
                ) : (
                  <p className="text-xs sm:text-sm leading-relaxed text-pretty whitespace-pre-wrap">
                    {aiInsights}
                  </p>
                )
              ) : (
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Analyzuji tvoje záznamy...
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  )
}
